import React from 'react';
import { Box } from '@mui/material';
import AIAnalysisDialog from './aiAnalysis/AIAnalysisDialog';
import RegressionSummaryReport from './aiAnalysis/RegressionSummaryReport';
import RegressionAnalysisFallback from './aiAnalysis/RegressionAnalysisFallback';
import ConfluenceExportJobsDialog from '../export/ConfluenceExportJobsDialog';
import { findReportByKeys, REGRESSION_REPORT_KEYS } from './aiAnalysis/aiReportUtils';
import useAIAnalysis, { AI_ANALYSIS_TYPES } from './aiAnalysis/useAIAnalysis';
import TestHistoryEmptyState from './list/TestHistoryEmptyState';
import TestHistoryErrorState from './list/TestHistoryErrorState';
import TestHistoryLoadingState from './list/TestHistoryLoadingState';
import TestHistoryResultsGrid from './list/TestHistoryResultsGrid';
import TestHistoryToolbar from './list/TestHistoryToolbar';

/**
 * TestHistoryList - Displays a grid of test result cards
 * @param {Array} results - Array of test results (filtered)
 * @param {Array} allResults - Array of all test results (unfiltered)
 * @param {number} totalResults - Total number of results before filtering
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message
 * @param {Function} onCardClick - Callback when a card is clicked
 * @param {string} sortBy - Current sort option
 * @param {Function} onSortChange - Callback when sort changes
 * @param {boolean} hasActiveFilters - Whether any filters are active
 * @param {Object} searchFilters - API search filters for Confluence export
 */
function TestHistoryList({
  results,
  allResults,
  totalResults,
  loading,
  error,
  onCardClick,
  sortBy,
  onSortChange,
  hasActiveFilters,
  searchFilters,
}) {
  const regressionId =
    searchFilters?.regression_test_id ||
    allResults?.[0]?.regression_test_id ||
    results?.[0]?.regression_test_id;

  const {
    open: aiOpen,
    setOpen: setAiOpen,
    loading: aiLoading,
    data: aiData,
    error: aiError,
    runAnalysis,
  } = useAIAnalysis(AI_ANALYSIS_TYPES.REGRESSION, regressionId);

  const regressionReport = findReportByKeys(aiData, REGRESSION_REPORT_KEYS);

  const handleAIAnalysis = () => {
    if (!regressionId) return;
    runAnalysis();
  };

  if (loading) {
    return <TestHistoryLoadingState />;
  }

  if (error) {
    return <TestHistoryErrorState error={error} />;
  }

  if (!results || results.length === 0) {
    return <TestHistoryEmptyState totalResults={totalResults} />;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <TestHistoryToolbar
        results={results}
        allResults={allResults || []}
        totalResults={totalResults}
        sortBy={sortBy}
        onSortChange={onSortChange}
        aiLoading={aiLoading}
        regressionId={regressionId}
        onAIAnalysis={handleAIAnalysis}
        hasActiveFilters={hasActiveFilters}
        searchFilters={searchFilters || {}}
      />

      <TestHistoryResultsGrid results={results} onCardClick={onCardClick} />
      <ConfluenceExportJobsDialog />
      <AIAnalysisDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        title="AI Regression Analysis"
        titleColor="secondary.main"
        loading={aiLoading}
        error={aiError}
      >
        {aiData &&
          (regressionReport ? (
            <RegressionSummaryReport report={regressionReport} regressionId={regressionId} />
          ) : (
            <RegressionAnalysisFallback data={aiData} regressionId={regressionId} />
          ))}
      </AIAnalysisDialog>
    </Box>
  );
}

export default TestHistoryList;
